import { FloatingPortal } from '@floating-ui/react'
import { Combobox } from '@headlessui/react'
import classNames from 'classnames'
import { Check } from 'iconoir-react'
import { twMerge } from 'tailwind-merge'
import { ComboBoxInputSearch } from './ComboBoxInputSearch'
import { UnreadBadge } from 'app/components/inbox/UnreadBadge'
import NameIcon from 'app/components/shared/NameIcon'
import { CustomOption } from 'app/components/shared/styled/CustomOption'
import Dots from 'app/components/shared/styled/Dots'
import { Tooltip } from 'app/components/Tooltip'
import { OptionType } from 'types/OptionType'

type ComboOptionsProps = {
  open: boolean
  forceOpen?: boolean
  value?: OptionType
  optionsContainerClassName?: string
  setPopperElement: (element: HTMLUListElement | null) => void
  floatingStyles: React.CSSProperties
  floatingProps: Record<string, unknown>
  showSearchInDropdown?: boolean
  comboDropdownInputRef: React.MutableRefObject<HTMLInputElement>
  onSearchValueChange?: (text: string) => void
  onClearSearch?: () => void
  loading: boolean
  options?: OptionType[]
  optionClassName?: string
  enableDeselectAllOption?: boolean
  deselectAllOptions?: OptionType[]
  multiple?: boolean
  onAddNewValue?: (val: string) => void
  checkboxSide?: `left` | `right`
  allowDeselectOption?: boolean
  addText?: string
}
export function ComboOptions({
  open,
  forceOpen,
  value,
  optionsContainerClassName,
  setPopperElement,
  floatingStyles,
  floatingProps,
  showSearchInDropdown = true,
  comboDropdownInputRef,
  onSearchValueChange,
  onClearSearch,
  loading,
  options,
  optionClassName,
  enableDeselectAllOption,
  deselectAllOptions = [{ name: `All`, id: `0`, value: `` }],
  multiple = false,
  onAddNewValue,
  checkboxSide = `right`,
  allowDeselectOption = false,
  addText = `Add new value`,
}: ComboOptionsProps) {
  const preventScrollingToInput = () => {
    comboDropdownInputRef.current?.focus({ preventScroll: true })
  }
  return open || forceOpen ? (
    <FloatingPortal>
      <Combobox.Options
        className={twMerge(`combobox-options w-[var(--button-width)]`, optionsContainerClassName)}
        onWheel={(evt) => evt.stopPropagation()}
        ref={setPopperElement}
        style={floatingStyles}
        {...floatingProps}
        static
      >
        {showSearchInDropdown && (
          <ComboBoxInputSearch
            comboDropdownInputRef={comboDropdownInputRef}
            handleSearchValueChange={onSearchValueChange}
            onClearSearch={onClearSearch}
          />
        )}
        {loading ? (
          <Dots />
        ) : !!options && options.length ? (
          options.map((option) => (
            <div
              key={`${option.id}-${option.name}`}
              className={
                enableDeselectAllOption &&
                deselectAllOptions.some((deselectOption) => deselectOption.value === option.value)
                  ? `filter-option-all`
                  : ``
              }
            >
              <Tooltip
                placement="top"
                message={option.meta?.isUsed ? option.meta?.tooltipForDisabledOption : undefined}
              >
                <Combobox.Option
                  value={option}
                  onClick={() => preventScrollingToInput()}
                  data-option-id={option.id}
                  disabled={option.meta?.isUsed || option.meta?.isDisabled}
                  className={({ active, selected }) =>
                    classNames(
                      `combobox-option`,
                      optionClassName,
                      active ? `bg-trn-100 ` : ``,
                      option.value === `integrated` && `!text-green-600`,
                      option.value === `notIntegrated` && `!text-red-600`,
                      (option.meta?.isUsed || option.meta?.isDisabled) && `!text-neutral-500`,
                    )
                  }
                >
                  {({ active, selected }) => (
                    <div
                      className={classNames(
                        `flex w-full items-center`,
                        checkboxSide === `right` ? `justify-between` : `flex-row-reverse justify-end`,
                      )}
                    >
                      {option.meta?.customComboOption == null ? (
                        <span className={classNames(`flex items-center whitespace-pre-wrap`)}>
                          {option.meta?.integrationStatus && (
                            <Tooltip
                              placement="left"
                              message={
                                option.meta?.integrationStatus === `integrated`
                                  ? `Active PMS Integration`
                                  : option.meta?.integrationStatus === `missingData`
                                    ? `PMS Integration Error`
                                    : `No Active PMS integration`
                              }
                            >
                              <span
                                className={classNames(
                                  `mr-4 inline-block h-2 w-2 rounded-full`,
                                  { [`bg-green-600`]: option.meta.integrationStatus === `integrated` },
                                  { [`bg-red-600`]: option.meta.integrationStatus === `notIntegrated` },
                                  { [`bg-yellow-600`]: option.meta.integrationStatus === `missingData` },
                                )}
                              ></span>
                            </Tooltip>
                          )}
                          <span
                            className={classNames(
                              `flex items-center gap-x-2 font-medium`,
                              checkboxSide === `right` ? `flex-row-reverse` : ``,
                            )}
                          >
                            <span className="flex flex-col">
                              <span>{option.name}</span>
                              {option.meta?.subtext && (
                                <span className="text-body-xs text-neutral-500">{option.meta.subtext}</span>
                              )}
                            </span>
                            {option.meta?.icon && (
                              <img alt="option icon" className="inline h-auto w-4" src={option.meta?.icon} />
                            )}
                            {option.meta?.nameInitial && (
                              <NameIcon
                                fullName={option.name[0]}
                                heightClassName={option.meta?.subtext ? `h-8` : `h-5`}
                                widthClassName={option.meta?.subtext ? `w-8` : `w-5`}
                                textClassName={option.meta?.textClassName}
                                backgroundClassName={option.meta?.backgroundClassName}
                                borderClassName={option.meta?.borderClassName}
                                isTiny
                              />
                            )}
                          </span>
                        </span>
                      ) : (
                        !multiple && <CustomOption selected={selected}>{option.meta.customComboOption}</CustomOption>
                      )}
                      {multiple ? (
                        option.meta?.customComboOption != null ? (
                          <CustomOption selected={selected}>{option.meta.customComboOption}</CustomOption>
                        ) : (
                          <div
                            className={classNames(`flex h-5 items-center`, checkboxSide === `right` ? `ml-4` : `mr-4`)}
                          >
                            <input
                              name="option-checkbox"
                              type="checkbox"
                              readOnly
                              checked={selected}
                              className="h-4 w-4 rounded border-trn-700 text-trn-700 outline-none focus:outline-none focus:ring-0 focus:ring-offset-0"
                            />
                          </div>
                        )
                      ) : allowDeselectOption ? (
                        <Check className={classNames(value && option.name === value.name ? `ml-4 block` : `hidden`)} />
                      ) : (
                        <div className="flex items-center gap-x-1">
                          <Check
                            className={classNames(value && option.name === value.name ? `ml-4 block` : `hidden`)}
                          />
                          {option.meta?.unread > 0 && (
                            <div className="text-right">
                              <UnreadBadge value={option.meta?.unread} />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </Combobox.Option>
              </Tooltip>
              {!!option.children &&
                option.children.map((childOption) => (
                  <div
                    key={`${childOption.id}-${childOption.name}`}
                    className={
                      enableDeselectAllOption &&
                      deselectAllOptions.some((deselectOption) => deselectOption.value === childOption.value)
                        ? `filter-option-all`
                        : ``
                    }
                  >
                    <Tooltip
                      placement="top"
                      message={childOption.meta?.isUsed ? childOption.meta?.tooltipForDisabledOption : undefined}
                    >
                      <Combobox.Option
                        value={childOption}
                        data-option-id={childOption.id}
                        onClick={() => preventScrollingToInput()}
                        disabled={option.meta?.isUsed || childOption.meta?.isUsed || childOption.meta?.isDisabled}
                        className={({ active, selected }) =>
                          classNames(
                            `combobox-option mx-6`,
                            optionClassName,
                            active ? `bg-trn-100 ` : ``,
                            childOption.value === `integrated` && `!text-green-600`,
                            childOption.value === `notIntegrated` && `!text-red-600`,
                            (option.meta?.isUsed || childOption.meta?.isUsed || childOption.meta?.isDisabled) &&
                              `!text-neutral-500`,
                          )
                        }
                      >
                        {({ selected }) => (
                          <div
                            className={classNames(
                              `flex w-full items-center`,
                              checkboxSide === `right` ? `justify-between` : `flex-row-reverse justify-end`,
                            )}
                          >
                            <span className={classNames(`flex items-center whitespace-pre-wrap`)}>
                              <span>{childOption.name}</span>
                            </span>
                            {multiple ? (
                              <div
                                className={classNames(
                                  `flex h-5 items-center`,
                                  checkboxSide === `right` ? `ml-4` : `mr-4`,
                                )}
                              >
                                <input
                                  name="option-checkbox"
                                  type="checkbox"
                                  readOnly
                                  checked={selected}
                                  className="h-4 w-4 rounded border-trn-700 text-trn-700 outline-none focus:outline-none focus:ring-0 focus:ring-offset-0"
                                />
                              </div>
                            ) : (
                              <Check
                                className={classNames(
                                  value && childOption.name === value.name ? `ml-4 block` : `hidden`,
                                )}
                              />
                            )}
                          </div>
                        )}
                      </Combobox.Option>
                    </Tooltip>
                    {!!childOption.children &&
                      childOption.children.map((grandChildOption) => (
                        <div
                          key={`${grandChildOption.id}-${grandChildOption.name}`}
                          className={
                            enableDeselectAllOption &&
                            deselectAllOptions.some((deselectOption) => deselectOption.value === grandChildOption.value)
                              ? `filter-option-all`
                              : ``
                          }
                        >
                          <Tooltip
                            placement="top"
                            message={
                              grandChildOption.meta?.isUsed
                                ? grandChildOption.meta?.tooltipForDisabledOption
                                : undefined
                            }
                          >
                            <Combobox.Option
                              value={grandChildOption}
                              data-option-id={grandChildOption.id}
                              onClick={() => preventScrollingToInput()}
                              disabled={
                                option.meta?.isUsed || childOption.meta?.isUsed || grandChildOption.meta?.isUsed
                              } // TODO: TRK-2201 is this what needs to be changed to fix campaigns not being selectable?
                              className={({ active, selected }) =>
                                classNames(
                                  `combobox-option mx-12 !py-1.5 !italic`,
                                  optionClassName,
                                  active ? `bg-trn-100 ` : ``,
                                  grandChildOption.value === `integrated` && `!text-green-600`,
                                  grandChildOption.value === `notIntegrated` && `!text-red-600`,
                                  (option.meta?.isUsed || childOption.meta?.isUsed || grandChildOption.meta?.isUsed) &&
                                    `!text-neutral-500`,
                                )
                              }
                            >
                              {({ selected }) => (
                                <div
                                  className={classNames(
                                    `flex w-full items-center`,
                                    checkboxSide === `right` ? `justify-between` : `flex-row-reverse justify-end`,
                                  )}
                                >
                                  <span className={classNames(`flex items-center whitespace-pre-wrap`)}>
                                    <span>{grandChildOption.name}</span>
                                  </span>
                                  {multiple ? (
                                    <div
                                      className={classNames(
                                        `flex h-5 items-center`,
                                        checkboxSide === `right` ? `ml-4` : `mr-4`,
                                      )}
                                    >
                                      <input
                                        name="option-checkbox"
                                        type="checkbox"
                                        readOnly
                                        checked={selected}
                                        className="h-4 w-4 rounded border-trn-700 text-trn-700 outline-none focus:outline-none focus:ring-0 focus:ring-offset-0"
                                      />
                                    </div>
                                  ) : (
                                    <Check
                                      className={classNames(
                                        value && grandChildOption.name === value.name ? `ml-4 block` : `hidden`,
                                      )}
                                    />
                                  )}
                                </div>
                              )}
                            </Combobox.Option>
                          </Tooltip>
                        </div>
                      ))}
                  </div>
                ))}
            </div>
          ))
        ) : (
          <span className="combobox-option !text-caption-md !text-neutral-500">No results found</span>
        )}
        {!!onAddNewValue && (
          <div
            onClick={() => onAddNewValue(comboDropdownInputRef.current?.value)}
            className="order-last cursor-pointer border-t border-trn-200 p-2 text-ui-xs text-trn-800 hover:bg-trn-100"
          >
            {`+ ${addText}`}
          </div>
        )}
      </Combobox.Options>
    </FloatingPortal>
  ) : (
    <></>
  )
}
